/**
 * Svea Payments (#2806, #2808, epic #2805).
 *
 * Each workshop signs its own contract with Svea Payments; OS Buddy provides
 * the integration. This tab sends Svea a contact request and, once the
 * contract exists, stores the merchant credentials. The company identity is
 * shown read-only because the server takes it from the workshop settings —
 * only the contact person and the message are editable here. The secret key
 * is write-only; the API never returns it.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import { ApiError } from '@/lib/api';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useNotify } from '@/hooks/useNotify';
import {
  SVEA_PRODUCTS,
  useDeleteSveaCredentials,
  useSaveSveaCredentials,
  useSendSveaContactRequest,
  useSveaIntegration,
  useTestSveaCredentials,
  type SveaCredentialsStatus,
  type SveaEnvironment,
  type SveaProduct,
} from '@/hooks/useSveaIntegration';
import { formatDateTime } from '@/utils/locale';
import { formatCentsForDisplay } from '@/utils/money';

const COOLDOWN_MS = 24 * 60 * 60 * 1000;

const PRODUCT_KEYS: Record<SveaProduct, { labelKey: string; descKey: string }> = {
  payment_link: { labelKey: 'svea.products.paymentLink', descKey: 'svea.products.paymentLinkDesc' },
  invoice: { labelKey: 'svea.products.invoice', descKey: 'svea.products.invoiceDesc' },
  part_payment: { labelKey: 'svea.products.partPayment', descKey: 'svea.products.partPaymentDesc' },
  b2b_invoice: { labelKey: 'svea.products.b2bInvoice', descKey: 'svea.products.b2bInvoiceDesc' },
};

const ENV_OPTIONS: ReadonlyArray<{ value: SveaEnvironment; labelKey: string }> = [
  { value: 'test', labelKey: 'svea.credentials.environmentTest' },
  { value: 'production', labelKey: 'svea.credentials.environmentProduction' },
];

const ERROR_KEYS: Record<string, string> = {
  SVEA_BUSINESS_ID_MISSING: 'svea.errors.businessIdMissing',
  SVEA_CONTACT_REQUEST_RECENT: 'svea.errors.recent',
  SVEA_LEAD_CHANNEL_NOT_CONFIGURED: 'svea.errors.notConfigured',
  SVEA_CONTACT_REQUEST_SEND_FAILED: 'svea.errors.sendFailed',
  SVEA_ENCRYPTION_KEY_MISSING: 'svea.errors.encryptionMissing',
  SVEA_SECRET_REQUIRED: 'svea.errors.secretRequired',
  SVEA_CREDENTIALS_MISSING: 'svea.errors.credentialsMissing',
  SVEA_CREDENTIALS_DECRYPT_FAILED: 'svea.errors.decryptFailed',
};

const LAST_TEST_ERROR_KEYS: Record<string, string> = {
  SVEA_TEST_REJECTED: 'svea.errors.testRejected',
  SVEA_TEST_HTTP: 'svea.errors.testHttp',
  SVEA_TEST_TIMEOUT: 'svea.errors.testTimeout',
  SVEA_TEST_NETWORK: 'svea.errors.testNetwork',
  SVEA_TEST_MALFORMED: 'svea.errors.testMalformed',
  SVEA_TEST_INVALID: 'svea.errors.testInvalid',
  SVEA_TEST_FAILED: 'svea.errors.testFailed',
};

function lastTestErrorText(error: string, t: (key: string) => string): string {
  const key = LAST_TEST_ERROR_KEYS[error];
  return key !== undefined ? t(key) : error;
}

function errorCode(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const data = error.data;
  if (data && typeof data === 'object' && 'code' in data) {
    const code = (data as { code: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

function credentialsStatusChip(credentials: SveaCredentialsStatus): {
  color: 'success' | 'warning' | 'error' | 'default';
  labelKey: string;
} {
  if (credentials.enabled) return { color: 'success', labelKey: 'svea.credentials.statusConnected' };
  if (credentials.lastTest?.success === false) {
    return { color: 'error', labelKey: 'svea.credentials.statusFailed' };
  }
  if (credentials.hasSecret) return { color: 'warning', labelKey: 'svea.credentials.statusUntested' };
  return { color: 'default', labelKey: 'svea.credentials.statusNotConfigured' };
}

export function SveaSettingsTab() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();

  const query = useSveaIntegration(true);
  const send = useSendSveaContactRequest();
  const saveCredentials = useSaveSveaCredentials();
  const testCredentials = useTestSveaCredentials();
  const deleteCredentials = useDeleteSveaCredentials();

  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [products, setProducts] = useState<SveaProduct[]>(['payment_link']);
  const [message, setMessage] = useState('');
  const [consent, setConsent] = useState(false);

  const [sellerId, setSellerId] = useState('');
  const [environment, setEnvironment] = useState<SveaEnvironment>('test');
  const [secretKey, setSecretKey] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    const prefill = query.data?.prefill;
    if (!prefill) return;
    setContactName((v) => v || prefill.contactName);
    setContactEmail((v) => v || prefill.contactEmail);
    setContactPhone((v) => v || prefill.contactPhone);
  }, [query.data?.prefill]);

  useEffect(() => {
    const credentials = query.data?.credentials;
    if (!credentials) return;
    setSellerId(credentials.sellerId ?? '');
    setEnvironment(credentials.environment ?? 'test');
    setSecretKey('');
  }, [query.data?.credentials]);

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress aria-label={tc('loading')} />
      </Box>
    );
  }
  if (query.isError || !query.data) {
    return <Alert severity="error">{t('svea.loadFailed')}</Alert>;
  }

  const { workshop, contactRequest, leadChannelConfigured, credentials } = query.data;
  const businessIdMissing = workshop.businessId === null;
  const recentlySent =
    contactRequest !== null && Date.now() - Date.parse(contactRequest.requestedAt) < COOLDOWN_MS;
  const address = [workshop.address, [workshop.postalCode, workshop.city].filter(Boolean).join(' ')]
    .filter((part) => part !== null && part !== '')
    .join(', ');

  const toggleProduct = (product: SveaProduct, checked: boolean) => {
    setProducts((current) =>
      checked ? SVEA_PRODUCTS.filter((p) => p === product || current.includes(p)) : current.filter((p) => p !== product),
    );
  };

  const formValid =
    contactName.trim() !== '' &&
    contactEmail.trim() !== '' &&
    contactPhone.trim().length >= 5 &&
    products.length > 0 &&
    consent;
  const canSend = leadChannelConfigured && !businessIdMissing && !recentlySent && formValid && !send.isPending;

  const handleSend = async () => {
    if (!canSend) return;
    try {
      await send.mutateAsync({
        contactName: contactName.trim(),
        contactEmail: contactEmail.trim(),
        contactPhone: contactPhone.trim(),
        products,
        ...(message.trim() ? { message: message.trim() } : {}),
        consent: true,
      });
      setConsent(false);
      setMessage('');
      notify.success(t('svea.sent'), t('svea.sentDesc'));
    } catch (error) {
      const key = ERROR_KEYS[errorCode(error) ?? ''] ?? 'svea.errors.sendFailed';
      notify.error(tc('error'), t(key));
    }
  };

  const canSaveCredentials =
    sellerId.trim() !== '' &&
    (secretKey !== '' || credentials.hasSecret) &&
    credentials.encryptionAvailable &&
    !saveCredentials.isPending;

  const handleSaveCredentials = async () => {
    if (!canSaveCredentials) return;
    try {
      await saveCredentials.mutateAsync({
        sellerId: sellerId.trim(),
        environment,
        ...(secretKey !== '' ? { secretKey } : {}),
      });
      setSecretKey('');
      notify.success(t('svea.credentials.saved'), t('svea.credentials.savedDesc'));
    } catch (error) {
      const key = ERROR_KEYS[errorCode(error) ?? ''] ?? 'svea.errors.saveFailed';
      notify.error(tc('error'), t(key));
    }
  };

  const canTest =
    credentials.hasSecret &&
    credentials.sellerId !== null &&
    credentials.encryptionAvailable &&
    !testCredentials.isPending &&
    !saveCredentials.isPending;

  const handleTest = async () => {
    if (!canTest) return;
    try {
      const result = await testCredentials.mutateAsync();
      if (result.success) {
        notify.success(t('svea.credentials.testSuccess'), t('svea.credentials.testSuccessDesc'));
      } else {
        notify.error(
          tc('error'),
          result.credentials.lastTest?.error !== undefined
            ? lastTestErrorText(result.credentials.lastTest.error, t)
            : t('svea.errors.testFailed'),
        );
      }
    } catch (error) {
      const key = ERROR_KEYS[errorCode(error) ?? ''] ?? 'svea.errors.testFailed';
      notify.error(tc('error'), t(key));
    }
  };

  const canDelete = (credentials.hasSecret || credentials.sellerId !== null) && !deleteCredentials.isPending;

  const handleDelete = async () => {
    try {
      await deleteCredentials.mutateAsync();
      setConfirmDelete(false);
      setSellerId('');
      setEnvironment('test');
      setSecretKey('');
      notify.success(t('svea.credentials.deleted'), t('svea.credentials.deletedDesc'));
    } catch (error) {
      const key = ERROR_KEYS[errorCode(error) ?? ''] ?? 'svea.errors.deleteFailed';
      notify.error(tc('error'), t(key));
    }
  };

  const statusChip = credentialsStatusChip(credentials);
  const methods = credentials.lastTest?.success === true ? (credentials.lastTest.methods ?? []) : [];

  return (
    <Stack spacing={3} sx={{ maxWidth: 720 }}>
      <Box>
        <Typography variant="h6" gutterBottom>
          {t('svea.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('svea.intro')}
        </Typography>
      </Box>

      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle2">{t('svea.credentials.title')}</Typography>
          <Chip size="small" color={statusChip.color} label={t(statusChip.labelKey)} />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {t('svea.credentials.intro')}
        </Typography>
        {credentials.enabled && <Alert severity="success">{t('svea.credentials.enabledHint')}</Alert>}
        {!credentials.encryptionAvailable && (
          <Alert severity="warning">{t('svea.credentials.encryptionMissing')}</Alert>
        )}
        {credentials.lastTest?.success === false && credentials.lastTest.error !== undefined && (
          <Alert severity="error">{lastTestErrorText(credentials.lastTest.error, t)}</Alert>
        )}

        <TextField
          label={t('svea.credentials.sellerId')}
          value={sellerId}
          onChange={(e) => setSellerId(e.target.value)}
          helperText={t('svea.credentials.sellerIdHint')}
          autoComplete="off"
          required
          fullWidth
        />
        <TextField
          select
          label={t('svea.credentials.environment')}
          value={environment}
          onChange={(e) => setEnvironment(e.target.value as SveaEnvironment)}
          fullWidth
        >
          {ENV_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {t(opt.labelKey)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={t('svea.credentials.secretKey')}
          type="password"
          value={secretKey}
          onChange={(e) => setSecretKey(e.target.value)}
          helperText={
            credentials.hasSecret ? t('svea.credentials.secretKeySetHint') : t('svea.credentials.secretKeyHint')
          }
          autoComplete="new-password"
          fullWidth
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="contained"
            startIcon={saveCredentials.isPending ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={() => void handleSaveCredentials()}
            disabled={!canSaveCredentials}
          >
            {t('svea.credentials.save')}
          </Button>
          <Button
            variant="outlined"
            onClick={() => void handleTest()}
            disabled={!canTest}
          >
            {t('svea.credentials.test')}
          </Button>
          <Button
            color="error"
            onClick={() => setConfirmDelete(true)}
            disabled={!canDelete}
          >
            {t('svea.credentials.delete')}
          </Button>
        </Stack>

        {methods.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('svea.credentials.methods')}
            </Typography>
            <Stack spacing={1}>
              {methods.map((method) => (
                <Stack key={method.code} direction="row" spacing={1} alignItems="center">
                  {method.imageUrl !== undefined && method.imageUrl !== '' && (
                    <Box
                      component="img"
                      src={method.imageUrl}
                      alt=""
                      sx={{ height: 24, width: 'auto' }}
                    />
                  )}
                  <Typography variant="body2">
                    {method.displayName} ({method.code})
                    {method.extraFeeCents !== undefined
                      ? ` · ${t('svea.credentials.extraFee', { amount: formatCentsForDisplay(method.extraFeeCents) })}`
                      : ''}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>

      <Divider />

      {contactRequest && (
        <Alert severity="success">
          {t('svea.requestSentAt', {
            date: formatDateTime(contactRequest.requestedAt),
            name: contactRequest.requestedByName,
          })}
        </Alert>
      )}
      {!leadChannelConfigured && <Alert severity="info">{t('svea.notConfigured')}</Alert>}
      {businessIdMissing && (
        <Alert severity="warning">
          {t('svea.businessIdMissing')}{' '}
          <Link component={RouterLink} to="/settings?tab=workshop">
            {t('svea.openWorkshopSettings')}
          </Link>
        </Alert>
      )}

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {t('svea.company')}
        </Typography>
        <Typography variant="body2">{workshop.companyName}</Typography>
        <Typography variant="body2" color="text.secondary">
          {workshop.businessId ?? '—'}
          {address ? ` · ${address}` : ''}
        </Typography>
        <FormHelperText>{t('svea.companyHint')}</FormHelperText>
      </Box>

      <Stack spacing={2}>
        <Typography variant="subtitle2">{t('svea.contactPerson')}</Typography>
        <TextField
          label={t('svea.contactName')}
          value={contactName}
          onChange={(e) => setContactName(e.target.value)}
          required
          fullWidth
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label={t('svea.contactEmail')}
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label={t('svea.contactPhone')}
            type="tel"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            required
            fullWidth
          />
        </Stack>
      </Stack>

      <FormControl component="fieldset" required error={products.length === 0}>
        <FormLabel component="legend">{t('svea.productsLabel')}</FormLabel>
        <FormGroup>
          {SVEA_PRODUCTS.map((product) => (
            <FormControlLabel
              key={product}
              control={
                <Checkbox
                  checked={products.includes(product)}
                  onChange={(e) => toggleProduct(product, e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{t(PRODUCT_KEYS[product].labelKey)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t(PRODUCT_KEYS[product].descKey)}
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', mb: 1, '& .MuiCheckbox-root': { pt: 0.5 } }}
            />
          ))}
        </FormGroup>
        {products.length === 0 && <FormHelperText>{t('svea.productsRequired')}</FormHelperText>}
      </FormControl>

      <TextField
        label={t('svea.message')}
        placeholder={t('svea.messagePlaceholder')}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        multiline
        minRows={3}
        inputProps={{ maxLength: 2000 }}
        fullWidth
      />

      <FormControlLabel
        control={<Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} />}
        label={<Typography variant="body2">{t('svea.consent')}</Typography>}
        sx={{ alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.5 } }}
      />

      <Box>
        <Button
          variant="contained"
          startIcon={send.isPending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          onClick={() => void handleSend()}
          disabled={!canSend}
        >
          {t('svea.send')}
        </Button>
        {recentlySent && <FormHelperText>{t('svea.recentHint')}</FormHelperText>}
      </Box>

      <ConfirmDialog
        open={confirmDelete}
        title={t('svea.credentials.deleteTitle')}
        message={t('svea.credentials.deleteMessage')}
        confirmColor="error"
        confirmText={tc('delete')}
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDelete(false)}
        loading={deleteCredentials.isPending}
      />
    </Stack>
  );
}
